import React from 'react';

import {REPORT} from '@constants/resources';
import {GetReportIncidentOptions, GetReportAnIncident, GetReports} from '@graphql/reports/query';
import {CreateReport, UpdateReport, DeleteReport} from '@graphql/reports/mutation';
import Form from '@appComponents/ReduxFormControls';
import * as messages from '@constants/messages';
import {IconInfoCircle} from '@appComponents/IconCollection';
import {INCIDENT_DURATION_UNIT_MINUTE} from '@constants/variables';
import {EventReportComment} from '@modules/events/components/EventsReport/EventReportComment';
import {hasUpdatePrivileges} from '@modules/events/forms/EventReportIncidentForm';

import validatorEventReportIncident from './validatorEventReportIncident';

function EventReportIncidentModel(isEditMode = false) {
    return {
        title: () => ('Report an incident'),
        formName: 'report',
        entityLabel: 'Incident',
        entityDataMapKey: 'report',
        resources: REPORT,
        showChangeLog: true,
        validate: validatorEventReportIncident,
        label: 'Incident',
        messages: {
            boxName: {
                delete: 'event',
                success: 'event',
                error: 'event',
            },
            text: {
                ...messages,
                DELETE_HEADER_ARGUMENT_FIRST: () => messages.DELETE_HEADER('incident'),
            },

        },
        mutation: {
            createEntity: CreateReport,
            updateEntity: UpdateReport,
            deleteEntity: DeleteReport,
        },
        mutationOptions: {
            create: {
                refetchQueries: [GetReports],
            },
            update: {
                refetchQueries: [GetReports],
            },
            delete: {
                refetchQueries: [GetReports],
            },
        },
        query: {
            getEntity: GetReportAnIncident,
            getOptions: GetReportIncidentOptions,
        },
        fields: {
            id: {
                dataMapKey: 'report.id',
                props: {
                    component: Form.IdField,
                    label: '',
                    name: 'id',
                    hidden: true,
                },
            },
            includes_all_streams: {
                dataMapKey: 'report.includes_all_streams',
                props: {
                    component: Form.SemanticInput,
                    label: 'All streams',
                    name: 'includes_all_streams',
                    type: 'toggle',
                    ...(isEditMode ? {} : {defaultValue: false}),
                    description: <IconInfoCircle
                        tooltip={'Switch on if the report is for all of the streams of the event.'}/>,
                },
            },
            report_scopes: {
                props: {
                    component: Form.SemanticCheckboxTree,
                    label: 'Scope',
                    name: 'report_scopes',
                    returnParsedTreeValues: true,
                },
            },
            incident_source: {
                props: {
                    component: Form.SemanticCheckboxList,
                    label: 'Source',
                    name: 'incident_source',
                    inline: true,
                    type: 'radio',
                    required: true,
                    defaultValue: [],
                },
            },
            incident_type: {
                props: {
                    component: Form.SemanticCheckboxList,
                    label: 'Type',
                    name: 'incident_type',
                    type: 'radio',
                    required: true,
                    defaultValue: [],
                },
            },
            entire_duration: {
                props: {
                    component: Form.SemanticInput,
                    defaultValue: false,
                    label: 'Entire duration',
                    name: 'entire_duration',
                    type: 'toggle',
                    description: <IconInfoCircle
                        tooltip={'Switch on if the incident lasted for the full duration of the event.'}/>,
                },
            },
            incident_duration_unit: {
                dataMapKey: 'report.incident_duration_unit.id',
                props: {
                    component: Form.SemanticInput,
                    label: '',
                    name: 'incident_duration_unit',
                    ...(isEditMode ? {} : {defaultValue: INCIDENT_DURATION_UNIT_MINUTE}),
                    readOnly: true,
                    hidden: true,
                },
            },
            incident_duration: {
                dataMapKey: 'report.incident_duration',
                props: {
                    component: Form.SemanticInput,
                    label: 'Duration',
                    name: 'incident_duration',
                    type: 'number',
                    required: true,
                },
            },
            count_as_downtime: {
                dataMapKey: 'report.count_as_downtime',
                props: {
                    component: Form.SemanticInput,
                    ...(isEditMode ? {} : {defaultValue: true}),
                    label: 'Count as downtime',
                    name: 'count_as_downtime',
                    type: 'toggle',
                    description: <IconInfoCircle
                        tooltip={'Switch on if the duration of the incident should be counted as a downtime.'}/>,
                },
            },
            comment: {
                dataMapKey: 'report.comment',
                props: {
                    component: EventReportComment,
                    label: 'Comment',
                    name: 'comment',
                    rows: 3,
                    hasUpdatePrivileges: hasUpdatePrivileges(),
                },
            },
        },
    };
}

export function EventReportIncidentAddModel() {
    return EventReportIncidentModel(false);
}

export function EventReportIncidentEditModel() {
    return EventReportIncidentModel(true);
}
